// Single source of truth for the authenticated user.
//
// Replaces ``useAuthState(auth)`` from react-firebase-hooks. Components call
// ``useAuth()`` to read ``{ user, status, signIn, signUp, signOut }``. The
// context is seeded from ``sessionStorage`` on mount (so a page reload stays
// signed in) and then refreshed from ``/api/auth/me/`` to confirm the token
// is still valid. A 401 during that fetch trips the auto-refresh in
// ``apiClient`` and, if that fails, the user is cleared.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import {
  clearSession,
  fetchMe,
  getStoredUser,
  login as loginRequest,
  register as registerRequest,
  signOut as signOutRequest,
} from '../util/auth';
import { SESSION_CLEARED_EVENT } from '../util/apiClient';
import { logger } from '../util/logger';
import { useT } from '../i18n/I18nContext';

const AuthContext = createContext({
  user: null,
  status: 'loading',
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  refresh: async () => {},
});

const STATUS = {
  LOADING: 'loading',
  SIGNED_IN: 'signedIn',
  SIGNED_OUT: 'signedOut',
};

export function AuthProvider({ children }) {
  const t = useT();
  const [user, setUser] = useState(() => getStoredUser());
  // Start in ``loading`` whenever there's a stored user we haven't yet
  // verified against ``/api/auth/me/``. Flipping optimistically to
  // ``signedIn`` let protected subtrees (Settings, etc.) mount and fire
  // their effects with a possibly-expired token, which showed up as 401s
  // on ``/api/users/findByEmail/`` and ``/api/notifications/`` before
  // RequireAuth could redirect. ``RequireAuth`` renders a spinner while
  // we're in this state, so protected routes don't mount until refresh
  // either confirms the session or clears it.
  const [status, setStatus] = useState(() =>
    getStoredUser() ? STATUS.LOADING : STATUS.SIGNED_OUT,
  );

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      setStatus(STATUS.SIGNED_IN);
      return me;
    } catch (err) {
      logger.warn('auth_me_failed', err?.message);
      clearSession();
      setUser(null);
      setStatus(STATUS.SIGNED_OUT);
      return null;
    }
  }, []);

  // Boot: if we have a stored user, verify the token silently.
  useEffect(() => {
    if (getStoredUser()) {
      refresh();
    } else {
      setStatus(STATUS.SIGNED_OUT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror ``status`` into a ref so the SESSION_CLEARED_EVENT handler can
  // decide whether to surface the toast WITHOUT carrying ``status`` in the
  // effect's deps array (which would re-bind the listener on every status
  // flip — extra work and a brief deafness window between unbind + rebind).
  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);

  // When ``apiClient``'s silent refresh fails, it wipes storage and
  // dispatches ``SESSION_CLEARED_EVENT``. Listen for it so any component
  // polling a protected resource (Notifications bell, e.g.) stops firing
  // 401s as soon as React sees the signed-out state. Also surface a
  // single toast to the user — pre-fix the same 401-after-refresh-failure
  // appeared as three separate UI degradations (save button stuck red,
  // Activity panel "unavailable", Versions panel empty list) without any
  // signal that the underlying cause was an expired session and that
  // re-signing-in would fix all three.
  useEffect(() => {
    const onCleared = () => {
      const wasSignedIn = statusRef.current === STATUS.SIGNED_IN;
      setUser(null);
      setStatus(STATUS.SIGNED_OUT);
      if (wasSignedIn) {
        toast.info(t('auth.toast.session_expired'), {
          // Toast id dedupes simultaneous fires — a 401 storm across
          // notifications + activity + versions used to enqueue 3+
          // toasts on top of each other; the id collapses them to one.
          toastId: 'session-expired',
          autoClose: 6000,
        });
      }
    };
    window.addEventListener(SESSION_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(SESSION_CLEARED_EVENT, onCleared);
  }, [t]);

  const signIn = useCallback(async (credentials) => {
    const me = await loginRequest(credentials);
    setUser(me);
    setStatus(STATUS.SIGNED_IN);
    return me;
  }, []);

  const signUp = useCallback(async (payload) => {
    const me = await registerRequest(payload);
    setUser(me);
    setStatus(STATUS.SIGNED_IN);
    return me;
  }, []);

  const signOut = useCallback(async () => {
    await signOutRequest();
    setUser(null);
    setStatus(STATUS.SIGNED_OUT);
  }, []);

  const value = useMemo(
    () => ({ user, status, signIn, signUp, signOut, refresh }),
    [user, status, signIn, signUp, signOut, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  return useContext(AuthContext);
}
